<template>
  <div>
    <HrPageLayout
      title="My Meetings"
      subtitle="View and respond to your meetings"
      icon="📅"
      :breadcrumb-items="[
        { label: 'Dashboard', route: '/employee-dashboard' },
        { label: 'My Meetings' }
      ]"
    >
      <template #header-actions>
        <button class="hr-btn hr-btn-primary" @click="loadMeetings">
          <i class="bx bx-refresh"></i> Refresh
        </button>
      </template>

      <div class="hr-page-content" style="padding: 20px;">
        <div class="hr-metrics-grid hr-mb-4">
          <div class="hr-metric-card gradient-1 hr-premium-card">
            <div class="hr-metric-icon"><i class="bx bx-calendar"></i></div>
            <div class="hr-metric-content">
              <p class="hr-metric-label">Total</p>
              <h2 class="hr-metric-value">{{ metrics.totalMeetings || 0 }}</h2>
            </div>
          </div>
          <div class="hr-metric-card gradient-2 hr-premium-card">
            <div class="hr-metric-icon"><i class="bx bx-calendar-week"></i></div>
            <div class="hr-metric-content">
              <p class="hr-metric-label">Upcoming (7 days)</p>
              <h2 class="hr-metric-value">{{ metrics.upcomingWeek || 0 }}</h2>
            </div>
          </div>
          <div class="hr-metric-card gradient-3 hr-premium-card">
            <div class="hr-metric-icon"><i class="bx bx-calendar-check"></i></div>
            <div class="hr-metric-content">
              <p class="hr-metric-label">Today</p>
              <h2 class="hr-metric-value">{{ metrics.todayMeetings || 0 }}</h2>
            </div>
          </div>
          <div class="hr-metric-card gradient-4 hr-premium-card">
            <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
            <div class="hr-metric-content">
              <p class="hr-metric-label">Pending response</p>
              <h2 class="hr-metric-value">{{ metrics.pendingResponses || 0 }}</h2>
            </div>
          </div>
        </div>

        <div class="hr-chart-card hr-mb-4 hr-premium-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-filter-alt"></i> Filter Meetings</h5>
          </div>
          <div class="hr-chart-body">
            <div class="hr-filters-grid">
              <div class="hr-filter-group">
                <label class="hr-form-label">From</label>
                <input v-model="filters.startDate" type="date" class="hr-input" @change="loadMeetings">
              </div>
              <div class="hr-filter-group">
                <label class="hr-form-label">To</label>
                <input v-model="filters.endDate" type="date" class="hr-input" @change="loadMeetings">
              </div>
              <div class="hr-filter-group">
                <label class="hr-form-label">Status</label>
                <select v-model="filters.status" class="hr-select" @change="loadMeetings">
                  <option value="">All</option>
                  <option value="Scheduled">Scheduled</option>
                  <option value="Completed">Completed</option>
                  <option value="Canceled">Canceled</option>
                </select>
              </div>
              <div class="hr-filter-group hr-flex-center" style="align-items: flex-end;">
                <button class="hr-btn hr-btn-primary" @click="loadMeetings" style="width: 100%;">
                  <i class="bx bx-refresh"></i> Refresh
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="hr-chart-card hr-premium-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-list-ul"></i> My Meetings</h5>
          </div>
          <div class="hr-chart-body" style="padding: 0;">
            <div v-if="loading" class="hr-loading-state hr-py-5">
              <div class="hr-spinner"></div>
              <p class="hr-text-muted hr-mt-2">Loading your meetings...</p>
            </div>

            <div v-else-if="!meetings.length" class="hr-meet-empty" style="margin: 20px; border: none; background: transparent;">
              <i class="bx bx-calendar-x"></i>
              <h5 class="hr-mt-2">No meetings found</h5>
              <p class="hr-text-muted">You have no meetings in the selected period.</p>
            </div>

            <div v-else class="hr-table-wrapper" style="padding: 0 20px;">
              <table class="hr-table-modern">
                <thead>
                  <tr>
                    <th>Meeting</th>
                    <th>Date & time</th>
                    <th>Location</th>
                    <th>Your response</th>
                    <th class="hr-text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="meeting in meetings" :key="meeting.id">
                    <td>
                      <div class="hr-flex-center">
                        <div class="hr-avatar-initials hr-avatar-primary hr-me-3">
                          <i class="bx bx-calendar"></i>
                        </div>
                        <div>
                          <strong style="color: #1e293b; display: block; margin-bottom: 2px;">{{ meeting.title }}</strong>
                          <span class="hr-small hr-text-muted">{{ meeting.meetingType || 'Meeting' }} · {{ meeting.organizer || '' }}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style="font-size: 13px; color: #334155;">
                        <span class="hr-flex-center hr-gap-1 hr-mb-1"><i class="bx bx-calendar-event hr-text-muted"></i> {{ meeting.formattedDate || meeting.startDate }}</span>
                        <span v-if="meeting.formattedStartTime" class="hr-small hr-text-muted hr-flex-center hr-gap-1">
                          <i class="bx bx-time hr-text-muted"></i> {{ meeting.formattedStartTime }}
                        </span>
                      </div>
                    </td>
                    <td style="font-size: 14px; color: #475569;">{{ meeting.location || meeting.virtualMeetingLink || '—' }}</td>
                    <td>
                      <span class="hr-badge" :class="getResponseBadgeClass(meeting.myResponse)">{{ meeting.myResponse || 'Pending' }}</span>
                    </td>
                    <td class="hr-text-center">
                      <div class="hr-flex-center hr-gap-2 hr-justify-center">
                        <template v-if="meeting.status === 'Scheduled' && (meeting.myResponse === 'Pending' || !meeting.myResponse)">
                          <button @click="respond(meeting.id, 'Accepted')" class="hr-btn hr-btn-sm hr-btn-success" :disabled="respondingId === meeting.id" title="Accept">
                            <i class="bx bx-check"></i> Accept
                          </button>
                          <button @click="respond(meeting.id, 'Declined')" class="hr-btn hr-btn-sm hr-btn-danger" :disabled="respondingId === meeting.id" title="Decline">
                            <i class="bx bx-x"></i> Decline
                          </button>
                        </template>
                        <span v-else class="hr-text-muted hr-small">—</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div v-if="pagination.totalPages > 1" class="hr-pagination-wrap hr-pb-3" style="padding-left: 20px; padding-right: 20px; padding-top: 10px;">
              <div class="hr-pagination-info">Page <strong>{{ pagination.currentPage }}</strong> of <strong>{{ pagination.totalPages }}</strong></div>
              <nav class="hr-pagination-nav">
                <button type="button" class="hr-page-btn" :disabled="pagination.currentPage <= 1"
                        @click="changePage(pagination.currentPage - 1)">
                  <i class="bx bx-chevron-left"></i>
                </button>
                <button v-for="page in visiblePages" :key="page" type="button" class="hr-page-btn"
                        :class="{ 'active': pagination.currentPage === page }" @click="changePage(page)">{{ page }}</button>
                <button type="button" class="hr-page-btn" :disabled="pagination.currentPage >= pagination.totalPages"
                        @click="changePage(pagination.currentPage + 1)">
                  <i class="bx bx-chevron-right"></i>
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </HrPageLayout>
  </div>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, toast } from "@/api";

export default {
  name: "MyMeetings",
  components: { HrPageLayout },
  data() {
    const today = new Date().toISOString().split("T")[0];
    const nextMonth = new Date();
    nextMonth.setDate(nextMonth.getDate() + 30);
    return {
      loading: true,
      respondingId: null,
      meetings: [],
      metrics: {},
      pagination: { currentPage: 1, totalPages: 1, totalMeetings: 0, limit: 10 },
      filters: {
        startDate: today,
        endDate: nextMonth.toISOString().split("T")[0],
        status: ""
      }
    };
  },
  computed: {
    visiblePages() {
      const total = this.pagination.totalPages;
      if (total <= 1) return [];
      const current = this.pagination.currentPage;
      const maxVisible = 5;
      let start = Math.max(1, current - Math.floor(maxVisible / 2));
      let end = Math.min(total, start + maxVisible - 1);
      if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
      const pages = [];
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    }
  },
  mounted() {
    this.loadMeetings();
  },
  methods: {
    loadMeetings() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getMyMeetings");
      data.append("startDate", this.filters.startDate);
      data.append("endDate", this.filters.endDate);
      data.append("page", this.pagination.currentPage);
      data.append("limit", this.pagination.limit);
      if (this.filters.status) data.append("status", this.filters.status);
      execute(data, authUrl)
        .then((res) => {
          this.loading = false;
          if (res.data.success) {
            this.meetings = res.data.data.meetings || [];
            this.metrics = res.data.data.metrics || {};
            this.pagination = res.data.data.pagination || this.pagination;
          } else {
            toast.error(res.data.message || "Failed to load meetings");
            this.meetings = [];
          }
        })
        .catch((err) => {
          this.loading = false;
          toast.error(err.message || "Failed to load meetings");
          this.meetings = [];
        });
    },
    changePage(page) {
      if (page < 1 || page > this.pagination.totalPages) return;
      this.pagination = { ...this.pagination, currentPage: page };
      this.loadMeetings();
    },
    getResponseBadgeClass(response) {
      const r = (response || "").toLowerCase();
      if (r === "accepted") return "hr-badge-success";
      if (r === "declined") return "hr-badge-danger";
      if (r === "tentative") return "hr-badge-warning";
      return "hr-badge-secondary";
    },
    respond(meetingId, response) {
      this.respondingId = meetingId;
      const data = new FormData();
      data.append("function", "respondToMeeting");
      data.append("meetingId", meetingId);
      data.append("response", response);
      execute(data, authUrl)
        .then((res) => {
          this.respondingId = null;
          if (res.data.success) {
            toast.success("Response recorded");
            this.loadMeetings();
          } else {
            toast.error(res.data.message || "Failed to update response");
          }
        })
        .catch((err) => {
          this.respondingId = null;
          toast.error(err.message || "Failed to update response");
        });
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";
.meet-filters { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; }
.hr-text-end { text-align: right; }
.hr-mr-1 { margin-right: 8px; }
</style>
